import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core'
import { LoadingController, ToastController } from '@ionic/angular'
import { Share } from '@capacitor/share'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import html2canvas from 'html2canvas'
import * as QRCode from 'qrcode'

@Component({
  selector: 'app-business-card',
  templateUrl: './business-card.component.html',
  styleUrls: ['./business-card.component.scss'],
})
export class BusinessCardComponent implements AfterViewInit, OnChanges {
  @Input() userData: any
  @ViewChild('qrCanvas') qrCanvas!: ElementRef

  public isFlipped = false
  public capturedImage: string | null = null
  public isPreparing = false
  public isSharing = false

  constructor(
    private loadingController: LoadingController,
    private toastController: ToastController,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userData'] && changes['userData'].currentValue) {
      // Clear previous capture on data change
      this.capturedImage = null
      setTimeout(() => {
        this.generateQRCode()
      }, 300)
    }
  }

  ngAfterViewInit() {
    this.generateQRCode()
  }

  toggleFlip() {
    this.isFlipped = !this.isFlipped
  }

  generateQRCode() {
    if (!this.userData?.publicId || !this.qrCanvas) return

    const profileUrl = `https://serviceathand.com/servicespage/${this.userData.publicId}`
    QRCode.toCanvas(
      this.qrCanvas.nativeElement,
      profileUrl,
      { width: 128, margin: 2, color: { dark: '#0f172a', light: '#ffffff' } },
      (error: any) => {
        if (error) console.error(error)
      }
    )
  }

  formatWhatsApp(phone: string): string {
    if (!phone) return ''
    const cleanPhone = phone.replace(/\D/g, '')
    if (cleanPhone.startsWith('0')) {
      return '27' + cleanPhone.substring(1)
    }
    return cleanPhone
  }

  private dataURItoBlob(dataURI: string) {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ab], { type: mimeString })
  }

  async saveContact() {
    const { name, phone, email, location, category } = this.userData

    // Create a robust vCard format
    const vCardData = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${name}`,
      `N:;${name};;;`,
      `ORG:Service at Hand - ${category || 'Professional'}`,
      `TEL;TYPE=CELL:${phone}`,
      `EMAIL;TYPE=INTERNET:${email}`,
      `ADR;TYPE=WORK:;;${location || 'South Africa'}`,
      `URL:https://serviceathand.com/servicespage/${this.userData.publicId}`,
      `NOTE:Professional Service Provider via Service at Hand`,
      'END:VCARD',
    ].join('\r\n')

    const fileName = `${name.replace(/\s+/g, '_')}.vcf`

    // NATIVE FLOW
    if (Capacitor.isNativePlatform()) {
      try {
        // Write the file to cache
        await Filesystem.writeFile({
          path: fileName,
          data: vCardData,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        })

        // Get the URI for sharing
        const savedFile = await Filesystem.getUri({
          directory: Directory.Cache,
          path: fileName,
        })

        // Use the Share API with the file URI
        await Share.share({
          title: `Save ${name} to Contacts`,
          text: `Contact information for ${name}`,
          files: [savedFile.uri],
        })
      } catch (error) {
        console.error('Native save contact failed', error)
        this.webSaveContact(vCardData, fileName)
      }
    }
    // WEB FLOW
    else {
      this.webSaveContact(vCardData, fileName)
    }
  }

  private async webSaveContact(vCardData: string, fileName: string) {
    try {
      const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)

      // Required for some browsers
      document.body.appendChild(link)
      link.click()

      // Cleanup
      setTimeout(async () => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        const toast = await this.toastController.create({
          message: 'Contact card ready! Please open to save.',
          duration: 3000,
          color: 'success',
          buttons: [{ text: 'OK', role: 'cancel' }],
        })
        await toast.present()
      }, 100)
    } catch (err) {
      console.error('Web save contact failed', err)
      const toast = await this.toastController.create({
        message: 'Could not generate contact card.',
        duration: 3000,
        color: 'danger',
      })
      await toast.present()
    }
  }

  async captureCard(): Promise<string | null> {
    const element = document.getElementById('premiumCaptureCard')
    if (!element) return null

    this.isPreparing = true
    this.cdr.detectChanges()

    try {
      // Use a slightly lower scale for performance and memory safety
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0f172a',
        logging: false,
        width: 1050,
        height: 600,
      })
      const dataUrl = canvas.toDataURL('image/png', 0.9)
      this.capturedImage = dataUrl
      return dataUrl
    } catch (error) {
      console.error('Capture engine error:', error)
      return null
    } finally {
      this.isPreparing = false
      this.cdr.detectChanges()
    }
  }

  async shareCard() {
    if (this.isSharing) return
    this.isSharing = true

    try {
      // 1. Check if we have pre-captured image
      let imageToShare = this.capturedImage

      // 2. If not ready, capture it now with a loading overlay
      if (!imageToShare) {
        const loading = await this.loadingController.create({
          message: 'Generating high-res card...',
          spinner: 'crescent',
        })
        await loading.present()
        try {
          imageToShare = await this.captureCard()
        } finally {
          await loading.dismiss()
        }
      }

      if (!imageToShare) throw new Error('Failed to generate image')

      const fileName = `BusinessCard_${this.userData.name.replace(
        /\s+/g,
        '_'
      )}.png`
      const base64Data = imageToShare.split(',')[1]

      // 3. NATIVE MOBILE FLOW
      if (Capacitor.isNativePlatform()) {
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
        })

        await Share.share({
          title: `${this.userData.name} - Business Card`,
          files: [savedFile.uri],
          dialogTitle: 'Share Virtual Card',
        })
      }
      // 4. WEB BROWSER FLOW
      else {
        const blob = this.dataURItoBlob(imageToShare)
        const file = new File([blob], fileName, { type: 'image/png' })
        const nav = navigator as any
        const url = `https://serviceathand.com/servicespage/${this.userData.publicId}`

        const fileShareData = {
          files: [file],
          title: `${this.userData.name} - Business Card`,
          text: `View Profile: ${url}`,
        }

        // Check for Web Share API support with Files
        if (nav.canShare && nav.canShare(fileShareData)) {
          try {
            await nav.share(fileShareData)
          } catch (err: any) {
            if (err.name === 'NotAllowedError') {
              // Fallback to clipboard if share is blocked (lost gesture)
              await this.copyLinkToClipboard(url)
              const toast = await this.toastController.create({
                message:
                  'Browser blocked sharing. Link copied to clipboard instead!',
                duration: 3000,
                color: 'medium',
              })
              await toast.present()
            } else if (err.name !== 'AbortError') {
              throw err
            }
          }
        } else {
          // Fallback: Try sharing just the link
          const linkShareData = {
            title: `${this.userData.name} - Business Card`,
            text: `View Profile: ${url}`,
            url: url,
          }

          if (nav.share && nav.canShare && nav.canShare(linkShareData)) {
            try {
              await nav.share(linkShareData)
            } catch (err: any) {
              if (err.name !== 'AbortError') {
                await this.copyLinkToClipboard(url)
              }
            }
          } else {
            // Web Share API not supported -> Clipboard
            await this.copyLinkToClipboard(url)
          }
        }
      }
    } catch (error: any) {
      console.error('Share operation failed:', error)
    } finally {
      this.isSharing = false
    }
  }

  async copyLinkToClipboard(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      const toast = await this.toastController.create({
        message: 'Sharing not supported. Link copied to clipboard!',
        duration: 3000,
        color: 'success',
      })
      await toast.present()
    } catch (e) {
      const toast = await this.toastController.create({
        message: 'Could not share. Please copy link manually.',
        duration: 3000,
        color: 'warning',
      })
      await toast.present()
    }
  }

  async downloadCard(providedImage?: string) {
    let base64Image = providedImage || this.capturedImage

    if (!base64Image) {
      const loading = await this.loadingController.create({
        message: 'Generating high-res card...',
      })
      await loading.present()
      base64Image = await this.captureCard()
      await loading.dismiss()
    }

    if (!base64Image) return

    const fileName = `BusinessCard_${this.userData.name.replace(
      /\s+/g,
      '_'
    )}.png`

    try {
      if (Capacitor.isNativePlatform()) {
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Image.split(',')[1],
          directory: Directory.Cache,
        })

        await Share.share({
          title: 'Save Business Card',
          url: savedFile.uri,
        })
      } else {
        const link = document.createElement('a')
        link.href = base64Image
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        const toast = await this.toastController.create({
          message: 'Professional card saved to downloads!',
          duration: 2000,
          color: 'success',
        })
        await toast.present()
      }
    } catch (error) {
      console.error('Save failed:', error)
    }
  }
}
